import React from "react";
import { List } from "components";
import {
  StatusCell,
  TypeCell,
  AmountCell,
  ProjectCell,
  JobCell,
  DateCell,
  ExtendedNameCell,
} from "@/modules/work/billables/cells";
import { EmployeeCell } from "components/cell/employee/cell";
import { addDays, formatISO, startOfDay, endOfDay } from "date-fns";
import { withPermissions } from "components/with-permisisons";
import { ROUTE } from "@/lib/utils";
import { usePermissions } from "contexts/permission";
import { getEmployeeFields } from "components/user/config";

const UpcomingBillablesList = ({ coordinatorId }) => {
  const permissions = usePermissions();

  const baseFields = [
    "id",
    "name",
    "type",
    "job.name",
    "job.project.name",
    "status",
    "billing_date",
    "due_date",
    "extensions.start_date",
    "extensions.billing_date",
    "extensions.justification",
    "extensions.date_created",
    "access.employee",
    "access.role",
    {
      job: [
        "id",
        getEmployeeFields("coordinator"),
        {
          project: [
            "id",
            "name",
            getEmployeeFields("coordinator"),
            getEmployeeFields("account_manager"),
            getEmployeeFields("sales_coordinator"),
            "project_id",
          ],
        },
      ],
    },
  ];

  const canReadAmount = permissions.hasFieldAccess(
    "billables",
    "read",
    "amount",
  );

  const fields = [
    ...baseFields,
    ...(canReadAmount ? ["amount", "currency.name", "adjusted_amount"] : []),
  ];

  const columns = [
    {
      id: "name",
      header: "Name",
      accessorKey: "name",
      cell: ExtendedNameCell,
    },
    {
      id: "job.project.name",
      header: "Project",
      accessorKey: "job.project.name",
      cell: ProjectCell,
    },
    {
      id: "job.name",
      header: "Job",
      accessorKey: "job.name",
      cell: JobCell,
    },
    {
      id: "amount",
      header: "Amount",
      accessorKey: "amount",
      cell: AmountCell,
      field: "amount",
    },
    {
      id: "status",
      header: "Status",
      accessorKey: "status",
      cell: StatusCell,
    },
    {
      id: "type",
      header: "Type",
      accessorKey: "type",
      cell: TypeCell,
    },
    {
      id: "billing_date",
      header: "Billing Date",
      accessorKey: "billing_date",
      sortable: true,
      cell: DateCell,
    },
    {
      id: "extensions_count",
      accessorKey: "extensions",
      header: "Extensions Count",
      cell: ({ getValue }) => getValue()?.length,
    },
    {
      id: "extensions",
      header: "Extension justification",
      accessorKey: "extensions",
      cell: ({ row }) => {
        const extensions = row.original.extensions || [];
        const lastExtension = extensions.length > 0 ? extensions[extensions.length - 1] : null;
        return lastExtension?.justification || '';
      },
    },
    {
      id: "job.coordinator",
      header: "JC",
      accessorKey: "job.coordinator",
      cell: EmployeeCell,
    },
    {
      id: "job.project.coordinator",
      header: "PC",
      accessorKey: "job.project.coordinator",
      cell: EmployeeCell,
    },
    {
      id: "job.project.account_manager",
      header: "AM",
      accessorKey: "job.project.account_manager",
      cell: EmployeeCell,
    },
    {
      id: "job.project.sales_coordinator",
      header: "SC",
      accessorKey: "job.project.sales_coordinator",
      cell: EmployeeCell,
    },
  ];

  const filters = {
    job: {
      access: {
        employee: {
          _eq: coordinatorId,
        },
        role: {
          _in: ["account_manager", "sales_coordinator", "project_coordinator", "job_coordinator"]
        },
      },
    },
    billing_date: {
      _gte: formatISO(startOfDay(new Date()), { representation: "date" }),
      _lte: formatISO(endOfDay(addDays(new Date(), 15)), {
        representation: "date",
      }),
    },
    status: {
      _nin: ["archived", "cancelled", "collected"],
    },
  };

  return (
    <div key={coordinatorId}>
      <List
        title="Upcoming Billables (Next 15 Days)"
        storageKey={`overview-upcoming-billables-${coordinatorId}`}
        endPoint="billables"
        filters={filters}
        columns={columns}
        meta={{ fields }}
        perPage={25}
        sortBy="billing_date"
        href={(item) => item.extensions?.length > 0 ? ROUTE.BILLABLE_EXTENSIONS(item.id) : ROUTE.BILLABLE(item.id)}
        hidePagination={true}
        showSearch={false}
      />
    </div>
  );
};

export default withPermissions(UpcomingBillablesList, {
  collection: "billables",
  action: "read",
});
